import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  CmsTournamentResponse,
  CreateCmsTournamentRequest,
  UpdateCmsTournamentRequest,
} from "@/types/tournament";

const tournamentsKey = (organizationId: string) => ["cms-tournaments", organizationId] as const;

/** Lists all tournaments (including inactive) for an organization. */
export function useCmsTournaments(organizationId: string) {
  return useQuery<CmsTournamentResponse[]>({
    queryKey: tournamentsKey(organizationId),
    queryFn: () =>
      apiRequest<CmsTournamentResponse[]>("GET", `/organizations/${organizationId}/tournaments`),
    enabled: !!organizationId,
  });
}

/** Bootstraps a tournament shell (name + REQUIRED slug); config continues in the Tournament Portal. */
export function useCreateCmsTournament(organizationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateCmsTournamentRequest) =>
      apiRequest<CmsTournamentResponse>("POST", `/organizations/${organizationId}/tournaments`, data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: tournamentsKey(organizationId) });
      toast({
        title: "Tournament created",
        description: `"${created.name}" is ready — its admins can now finish the setup in the Tournament Portal.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create the tournament. Please try again.",
        variant: "destructive",
      });
    },
  });
}

/** Updates shell fields (name, slug, dates, active flag). */
export function useUpdateCmsTournament(organizationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ tournamentId, data }: { tournamentId: string; data: UpdateCmsTournamentRequest }) =>
      apiRequest<CmsTournamentResponse>(
        "PUT",
        `/organizations/${organizationId}/tournaments/${tournamentId}`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tournamentsKey(organizationId) });
      toast({ title: "Tournament updated" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update the tournament. Please try again.",
        variant: "destructive",
      });
    },
  });
}
